import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInput from "shared/FormInput";
import Button from "shared/Button";
import { ChevronDown, X } from "lucide-react";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import { addUser } from "core/Services/AdminServices";
import { toast } from "react-toastify";
import { Switch } from "shared/Switch";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

interface UserFormData {
  password: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_number: string;
  role: string;
  active: boolean;
}

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddUserDialog = ({ isOpen, onClose, onSuccess }: AddUserDialogProps) => {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const phones = [
    { value: "+962", label: "+962 Jordan" },
    { value: "+966", label: "+966 Saudi Arabia" },
    { value: "+971", label: "+971 United Arab Emirates" },
    { value: "+965", label: "+965 Kuwait" },
    { value: "+973", label: "+973 Bahrain" },
    { value: "+974", label: "+974 Qatar" },
    { value: "+968", label: "+968 Oman" },
    { value: "+964", label: "+964 Iraq" },
    { value: "+963", label: "+963 Syria" },
    { value: "+961", label: "+961 Lebanon" },
    { value: "+970", label: "+970 Palestine" },
    { value: "+20", label: "+20 Egypt" },
    { value: "+90", label: "+90 Turkey" },
    { value: "+98", label: "+98 Iran" },
    { value: "+967", label: "+967 Yemen" },
    { value: "+249", label: "+249 Sudan" },
  ];
  const baseSchema = {
    first_name: yup
      .string()
      .required(t("addUserDialog.validation.firstNameRequired"))
      .trim(),
    last_name: yup
      .string()
      .required(t("addUserDialog.validation.lastNameRequired"))
      .trim(),
    email: yup
      .string()
      .email(t("addUserDialog.validation.invalidEmail"))
      .required(t("addUserDialog.validation.emailRequired"))
      .trim(),
    phone: yup
      .string()
      .required(t("addUserDialog.validation.phoneRequired"))
      .matches(/^\+\d+$/, t("addUserDialog.validation.invalidPhoneCode")),
    phone_number: yup
      .string()
      .required(t("addUserDialog.validation.phoneNumberRequired"))
      .matches(/^\d+$/, t("addUserDialog.validation.invalidPhoneNumber"))
      .transform((value) => (value === "" ? null : value)),
    role: yup.string().required(t("addUserDialog.validation.roleRequired")),
    active: yup.boolean().default(false),
  };
  const CreateUserFormSchema = yup.object().shape({
    ...baseSchema,
    password: yup
      .string()
      .required(t("addUserDialog.validation.passwordRequired"))
      .min(8, t("addUserDialog.validation.passwordMinLength")),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm<UserFormData>({
    resolver: yupResolver(CreateUserFormSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "+962",
      phone_number: "",
      role: "",
      password: "",
      active: true,
    },
  });

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "operator", label: "Operator" },
    { value: "driver", label: "Driver" },
    { value: "accountant", label: "Accountant" },
  ];

  const handleFormSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);
    try {
      await addUser({
        ...data,
        active: isActive,
        password: data.password || "",
        phone_number: `${data.phone}${data.phone_number}`,
      });
      toast.success(t("addUserDialog.success"));
      onSuccess();
      handleClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("addUserDialog.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    clearErrors();
    onClose();
    reset({
      first_name: "",
      last_name: "",
      email: "",
      phone: "+962",
      phone_number: "",
      role: "",
      password: "",
      active: true,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>{t("addUserDialog.title")}</span>
            <button
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="h-full p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              name="first_name"
              label={t("addUserDialog.firstName")}
              placeholder={t("addUserDialog.enterFirstName")}
              register={register}
              errors={errors}
            />
            <FormInput
              name="last_name"
              label={t("addUserDialog.lastName")}
              placeholder={t("addUserDialog.enterLastName")}
              register={register}
              errors={errors}
            />

            <FormInput
              name="email"
              label={t("addUserDialog.email")}
              type="email"
              placeholder={t("addUserDialog.enterEmail")}
              register={register}
              errors={errors}
            />

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:w-1/4">
                <FormInput
                  name="phone"
                  label={t("addUserDialog.phone")}
                  type="select"
                  register={register}
                  errors={errors}
                  selectOptions={phones}
                  icon={<ChevronDown size={20} />}
                />
              </div>
              <div className="w-full sm:w-3/4">
                <FormInput
                  label={t("addUserDialog.phoneNumber")}
                  name="phone_number"
                  type="text"
                  placeholder={t("addUserDialog.enterPhoneNumber")}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>

            <FormInput
              name="role"
              label={t("addUserDialog.role")}
              type="select"
              placeholder={t("addUserDialog.selectRole")}
              icon={<ChevronDown size={20} />}
              register={register}
              errors={errors}
              selectOptions={roleOptions}
            />

            <FormInput
              name="password"
              label={t("addUserDialog.password")}
              type="password"
              placeholder={t("addUserDialog.enterPassword")}
              register={register}
              errors={errors}
            />

            <div className="flex items-center gap-2">
              <label htmlFor="active-status" className="text-sm font-medium">
                {t("addUserDialog.activeStatus")}
              </label>
              <Switch
                id="active-status"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <span className="text-sm text-gray-500">
                {isActive
                  ? t("addUserDialog.active")
                  : t("addUserDialog.deactivated")}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("addUserDialog.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {t("addUserDialog.addUser")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserDialog;
